import React, { useCallback, useMemo, useState } from 'react';

import { Confirm, Modal, PageTitle } from '@components/common';
import Table from '@components/common/Table/Table';

import { Icons } from '@icons/icons';

import { useMutateDeleteDownloadHistory } from '@hooks/downloadHistory/useMutateDeleteDownloadHistory';
import { useMutateExportDownloadHistory } from '@hooks/downloadHistory/useMutateExportDownloadHistory';
import { useQueryDownloadHistory } from '@hooks/downloadHistory/useQueryDownloadHistory';
import { useQueryDownloadHistorySources } from '@hooks/downloadHistory/useQueryDownloadHistorySources';
import { useQueryDownloadHistoryStatuses } from '@hooks/downloadHistory/useQueryDownloadHistoryStatuses';

import { getCustomActionsDownloadHistory } from '@utils/downloadHistory/getCustomActions';
import { getColumns } from '@components/routes/AdminPanel/DownloadHistory/utils';

import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	IActionMenu,
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';
import { IDownloadHistoryItem } from '@interfaces/downloadHistory/downloadHistoryDTO';

import { defaultMeta, EAdminPanelPageTitles, TMenuActionsTypes } from '@enums/common';

import { MIN_FILTER_WIDTH } from '@constants/common';
import { MODAL_WIDTH_480 } from '@constants/modals';

import styles from './DownloadHistory.module.scss';

const DownloadHistory = () => {
	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState('');
	const [selectedStatus, setSelectedStatus] = useState<TSelectOption<string> | null>(null);
	const [selectedSource, setSelectedSource] = useState<TSelectOption<string> | null>(null);
	const [isDeleteConfirm, setIsDeleteConfirm] = useState(false);
	const [selectedFile, setSelectedFile] = useState<IDownloadHistoryItem | null>(null);
	const [checkedIds, setCheckedIds] = useState<Array<string>>([]);
	const [actionType, setActionType] = useState<TMenuActionsTypes>();

	const {
		downloadHistory,
		countOfRecords,
		isLoadingDownloadHistory,
	} = useQueryDownloadHistory({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		status: selectedStatus?.value,
		source: selectedSource?.value,
	});

	const { sourcesOptions, isLoadingSourcesOptions } = useQueryDownloadHistorySources();
	const { statusesOptions, isLoadingStatusesOptions } = useQueryDownloadHistoryStatuses();

	const searchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
		setPage(defaultMeta.page);
	}, []);

	const pageHandler = useCallback((page: number) => setPage(page), []);

	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [page, pageSize, countOfRecords, pageHandler, pageSizeHandler]);

	const handleStatusChange = useCallback((option: TSelectOption<string>) => {
		setSelectedStatus(option);
		setPage(defaultMeta.page);
	}, []);

	const handleSourceChange = useCallback((option: TSelectOption<string>) => {
		setSelectedSource(option);
		setPage(defaultMeta.page);
	}, []);

	const filters: TTableFilter[] = useMemo(() => {
		return [
			{
				name: 'status',
				placeholder: 'Status',
				option: selectedStatus,
				options: statusesOptions,
				minWidth: MIN_FILTER_WIDTH,
				setState: handleStatusChange,
				defaultValue: '',
			},
			{
				name: 'source',
				placeholder: 'Source',
				option: selectedSource,
				options: sourcesOptions,
				minWidth: MIN_FILTER_WIDTH,
				setState: handleSourceChange,
				defaultValue: '',
			},
		];
	}, [
		handleSourceChange,
		handleStatusChange,
		selectedSource,
		selectedStatus,
		sourcesOptions,
		statusesOptions,
	]);

	const search: Array<ITableSearch> = useMemo(() => {
		return [{
			initialValue: searchValue,
			name: 'name',
			handler: searchHandler,
			tooltipText: 'Search by file name',
		}];
	}, [searchValue, searchHandler]);

	const openDeleteConfirm = useCallback((file: IDownloadHistoryItem | null) => {
		setSelectedFile(file);
		setIsDeleteConfirm(true);
	}, []);

	const closeDeleteConfirm = useCallback(() => {
		setIsDeleteConfirm(false);
		setSelectedFile(null);
		setActionType(undefined);
		setCheckedIds([]);
	}, []);

	const onSuccessDelete = useCallback(() => {
		closeDeleteConfirm();
		setPage(defaultMeta.page);
	}, [closeDeleteConfirm]);

	const {
		deleteFiles,
		isLoadingDeleteDownloadHistory,
	} = useMutateDeleteDownloadHistory({
		onSuccess: onSuccessDelete,
		source: selectedSource?.value,
		status: selectedStatus?.value,
		searchValue,
	});

	const {
		exportFiles,
		isLoadingExportDownloadHistory,
	} = useMutateExportDownloadHistory({
		source: selectedSource?.value,
		status: selectedStatus?.value,
		searchValue,
	});

	const handleDeleteFiles = useCallback(() => {
		deleteFiles({
			ids: selectedFile ? [selectedFile.id] : checkedIds,
			actionType: actionType,
		});
	}, [
		selectedFile,
		deleteFiles,
		checkedIds,
		actionType,
	]);

	const handleCustomDelete = useCallback((data: IGroupOperationWithActionDTO) => {
		setIsDeleteConfirm(true);
		setCheckedIds(data.ids.filter(Boolean));
		setActionType(data.actionType);
	}, []);

	const handleExportFiles = useCallback((ids: Array<string>, actionType: TMenuActionsTypes) => {
		const fileIds = downloadHistory
			.filter(file => ids.includes(file.id)).map(file => file.fileId)
			.map(fileId => fileId);

		exportFiles({
			ids: fileIds,
			actionType,
		});
	}, [downloadHistory, exportFiles]);

	const handleExportFile = useCallback((file: IDownloadHistoryItem | null) => {
		if (file?.id) {
			exportFiles({ ids: [file.fileId] });
		}
	}, [exportFiles]);

	const columns = getColumns({
		openDeleteConfirm,
		handleExportFile,
	});

	const customActions: IActionMenu<TMenuActionsTypes>[] = useMemo(() => {
		return getCustomActionsDownloadHistory({ downloadHistory, selectedStatus, checkedIds });
	}, [downloadHistory, selectedStatus, checkedIds]);

	const isLoadingTable = isLoadingDownloadHistory
		|| isLoadingSourcesOptions
		|| isLoadingStatusesOptions
		|| isLoadingExportDownloadHistory;

	return (
		<div className={styles.downloadHistoryWrapper}>
			<Modal
				isLoading={isLoadingDeleteDownloadHistory}
				isOpen={isDeleteConfirm}
				onClose={closeDeleteConfirm}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={closeDeleteConfirm}
					onSubmit={handleDeleteFiles}
					question={`Are you sure you want to delete the file${selectedFile ? '' : 's'}?`}
				/>
			</Modal>
			<PageTitle
				icon={Icons.settingsPainted({ innerSvgProps: true } as any)}
				title={EAdminPanelPageTitles.DownloadHistory}
			/>
			<div className={styles.downloadHistoryTableContainer}>
				<Table
					columns={columns}
					customExportActions={customActions}
					data={downloadHistory}
					filters={filters}
					handleCustomDelete={handleCustomDelete}
					handleExport={handleExportFiles}
					isExport
					isLoading={isLoadingTable}
					isPinColumns
					pagination={pagination}
					search={search}
					searchThreshold={0}
					setCheckedIds={setCheckedIds}
					sort={sort}
					stickyLeftColumnIndexes={[0, 1]}
				/>
			</div>
		</div>
	);
};

export default DownloadHistory;
